import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Link from "@/components/feature/LocaleLink";
import imgDashboard from "@/assets/images/solutions/nms-dashboard.jpg";

// 화면에 들어올 때 0에서 목표값까지 올라가는 숫자
// 기본값은 목표값: 사전 렌더링 HTML(검색엔진 · 링크 미리보기 · JS 미실행)과 스크롤 전 화면에 0 이 남지 않게 한다.
// 세는 동작은 감싼 [data-reveal] 영역이 나타나기 시작할 때(Effects.tsx 와 같은 기준) 0 부터 시작해,
// 투명한 상태에서 0 으로 바뀌므로 목표값이 보였다가 0 으로 튀는 깜빡임이 없다.
function CountUp({ target, suffix = "", text }: { target?: number; suffix?: string; text?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [value, setValue] = useState(target ?? 0);
  const decimals = target && !Number.isInteger(target) ? 1 : 0;

  useEffect(() => {
    const el = ref.current;
    if (target === undefined || !el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || !("IntersectionObserver" in window)) return;
    const area = el.closest<HTMLElement>("[data-reveal]");
    if (!area || area.classList.contains("is-visible")) return;
    let raf = 0;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        setValue(0);
        let start = 0;
        const tick = (now: number) => {
          if (!start) start = now;
          const p = Math.min(1, (now - start) / 1600);
          const eased = 1 - Math.pow(1 - p, 3);
          setValue(target * eased);
          if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      },
      { threshold: 0.1, rootMargin: "0px 0px -6% 0px" }
    );
    io.observe(area);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [target]);

  return (
    <span ref={ref} className="tabular-nums">
      {text ?? `${value.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${suffix}`}
    </span>
  );
}

// 한 줄 메시지 + 벤토 그리드(크기가 다른 타일)로 핵심 숫자 · 플랫폼 · 헬프데스크를 배치
export default function IntroSection() {
  const { t } = useTranslation();

  const tile = "glow-card glow-card-light rounded-xl border border-background-200 bg-background-100 p-6 md:p-7 flex flex-col justify-between overflow-hidden";

  return (
    <section id="intro" className="bg-background-50 py-24 md:py-32">
      <div className="max-w-6xl mx-auto px-4 md:px-10">
        <div className="text-center" data-reveal>
          <p className="text-xs tracking-[0.3em] font-semibold text-[var(--brand-orange)] uppercase mb-6 font-label">{t("intro.eyebrow")}</p>
          <h2 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-snug">
            {t("intro.line1")}
            <br />
            <span className="text-primary-600">{t("intro.line2")}</span>
          </h2>
          <p className="mt-6 text-foreground-600 text-base md:text-lg leading-relaxed max-w-3xl mx-auto">{t("intro.desc")}</p>
        </div>

        {/* 벤토 그리드: 4열 × 2행. 큰 타일(2×2) 에 대표 숫자, 나머지는 보조 숫자 · 플랫폼 · 헬프데스크 */}
        <div className="grid grid-cols-2 md:grid-cols-4 md:auto-rows-[200px] gap-4 mt-14" data-reveal data-reveal-stagger>
          <div className="glow-card col-span-2 md:row-span-2 relative rounded-xl overflow-hidden bg-navy-gradient text-background-50 p-7 md:p-9 flex flex-col justify-end min-h-[280px]">
            <img
              src={imgDashboard}
              alt=""
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover object-left-top opacity-[0.18] mix-blend-luminosity pointer-events-none"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/60 to-transparent pointer-events-none"></div>
            <div className="relative">
              <div className="text-accent-300 text-xs tracking-[0.3em] font-semibold font-label mb-3">FLEET UNDER MANAGEMENT</div>
              <div className="font-heading text-6xl md:text-7xl font-bold leading-none">
                <CountUp target={200} suffix="+" />
              </div>
              <div className="mt-3 text-base md:text-lg font-semibold">{t("intro.stat1")}</div>
              <div className="mt-1 text-sm text-foreground-300">{t("intro.fleetNote")}</div>
            </div>
          </div>

          <div className={tile}>
            <i className="ri-ship-2-line text-2xl text-primary-600"></i>
            <div>
              <div className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
                <CountUp target={15360} />
              </div>
              <div className="mt-1 text-sm text-foreground-600 leading-snug">{t("intro.stat2")}</div>
            </div>
          </div>

          <div className={tile}>
            <i className="ri-remote-control-line text-2xl text-primary-600"></i>
            <div>
              <div className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
                <CountUp target={93.5} suffix="%" />
              </div>
              <div className="mt-1 text-sm text-foreground-600 leading-snug">{t("intro.stat3")}</div>
            </div>
          </div>

          <a href="tel:+827075880611" className={`${tile} cursor-pointer hover:border-primary-300 transition-colors`}>
            <i className="ri-customer-service-2-line text-2xl text-primary-600"></i>
            <div>
              <div className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">24/365</div>
              <div className="mt-1 text-sm text-foreground-600 leading-snug">{t("intro.stat4")}</div>
              <div className="mt-1 text-xs text-primary-600 font-semibold font-label">070-7588-0611</div>
            </div>
          </a>

          <Link to="/solutions#remote" className={`${tile} cursor-pointer bg-primary-50 border-primary-100 hover:border-primary-300 transition-colors`}>
            <i className="ri-radar-line text-2xl text-primary-600"></i>
            <div>
              <div className="font-heading text-base md:text-lg font-bold text-foreground-950 leading-snug">{t("intro.platform.title")}</div>
              <div className="mt-1 text-xs md:text-sm text-foreground-600 leading-snug">{t("intro.platform.desc")}</div>
            </div>
          </Link>
        </div>
        <p className="mt-4 text-xs text-foreground-500 text-center">{t("intro.note")}</p>

        <div className="text-center">
          <Link
            to="/about"
            className="inline-flex items-center gap-2 mt-8 text-sm font-semibold text-primary-600 hover:gap-3 transition-all cursor-pointer"
          >
            {t("intro.more")}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
